import React from 'react';

/**
 * Last-resort boundary around the whole app.
 *
 * Without this, any throw during render unmounts the entire React tree and the
 * window is left permanently blank white with nothing in the UI to explain it.
 * That failure mode is indistinguishable from a hang, which makes bugs like the
 * events media-upload crash impossible to diagnose from a user report.
 *
 * The boundary keeps the crash on screen (message + component stack) and also
 * mirrors it into `localStorage` under DIAGNOSTIC_KEY so it survives a reload
 * and can be read back later.
 */

export const DIAGNOSTIC_KEY = 'nami:last-crash';

type CrashRecord = {
  kind: 'render' | 'error' | 'unhandledrejection';
  message: string;
  stack?: string;
  componentStack?: string;
  at: string;
  url: string;
};

export function recordCrash(record: CrashRecord): void {
  try {
    window.localStorage.setItem(DIAGNOSTIC_KEY, JSON.stringify(record));
  } catch {
    // storage full or blocked — the on-screen copy is still there
  }
  // eslint-disable-next-line no-console
  console.error('[nami crash]', record);
}

let globalHandlersInstalled = false;

/**
 * Captures errors and promise rejections that never reach a React boundary —
 * e.g. a rejected `void somethingAsync()` call — so they are recorded rather
 * than silently swallowed by the console.
 */
export function installGlobalErrorDiagnostics(): void {
  if (globalHandlersInstalled || typeof window === 'undefined') return;
  globalHandlersInstalled = true;

  window.addEventListener('error', (event) => {
    recordCrash({
      kind: 'error',
      message: String(event.message || event.error),
      stack: event.error?.stack,
      at: new Date().toISOString(),
      url: window.location.href,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason: any = event.reason;
    recordCrash({
      kind: 'unhandledrejection',
      message: String(reason?.message || reason),
      stack: reason?.stack,
      at: new Date().toISOString(),
      url: window.location.href,
    });
  });
}

type Props = { children: React.ReactNode };
type State = { error: Error | null; componentStack: string | null };

class RootErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? null });
    recordCrash({
      kind: 'render',
      message: error.message,
      stack: error.stack,
      componentStack: info.componentStack ?? undefined,
      at: new Date().toISOString(),
      url: window.location.href,
    });
  }

  render() {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="fixed inset-0 z-[9999] overflow-auto bg-white p-6 text-slate-800">
        <h1 className="text-lg font-semibold text-rose-700">Something broke on this screen</h1>
        <p className="mt-1 text-sm text-slate-600">
          The page stopped instead of going blank. Copy the details below when reporting this.
        </p>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-xl bg-indigo-600 px-3 py-2 text-sm font-medium text-white"
          >
            Reload app
          </button>
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard?.writeText(
                `${error.message}\n\n${error.stack ?? ''}\n\n${componentStack ?? ''}`,
              );
            }}
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700"
          >
            Copy details
          </button>
        </div>
        <pre className="mt-4 whitespace-pre-wrap break-words rounded-xl bg-slate-50 p-3 text-xs text-slate-700">
{error.message}
{'\n\n'}
{error.stack}
{'\n\n'}
{componentStack}
        </pre>
      </div>
    );
  }
}

export default RootErrorBoundary;
